import type { ReactNode } from 'react'
import { AppShell } from '@mantine/core'
import { useLingui } from '@lingui/react'
import { AuthWrapper as SharedAuthWrapper } from '@m3-tech/boss-api'
import { LoadingSpinner, TopNav, MODULE_HEADER_HEIGHT } from '@m3-tech/boss-ui'
import { useColorScheme } from '@m3-tech/boss-hooks'
import { hasModuleFeatureAccess } from '@/utils/featureAccess'
import AccessDenied from '@/pages/AccessDenied'
import { loadCatalog, locales, type SupportedLocale } from '@/i18n'

interface AuthWrapperProps {
    children: ReactNode
}

function AccessDeniedLayout({ children }: { children: ReactNode }) {
    const colorScheme = useColorScheme()

    // No MemoryRouter here: unlike the boilerplate, AuthWrapper renders inside the
    // RouterProvider (via AuthLayout), and a nested router throws — which trips the
    // shared AccessDeniedErrorBoundary and strips the TopNav shell.
    return (
        <AppShell header={{ height: MODULE_HEADER_HEIGHT }} padding="md">
            <AppShell.Header
                bg={colorScheme === 'dark' ? 'dark.6' : 'gray.1'}
                style={{ borderBottom: 'none' }}
            >
                <TopNav
                    currentModuleKey={import.meta.env.VITE_MODULE_NAME?.toLowerCase() ?? 'module'}
                    testIdBase="module"
                />
            </AppShell.Header>
            <AppShell.Main>{children}</AppShell.Main>
        </AppShell>
    )
}

/**
 * Module-specific AuthWrapper that delegates to the shared AuthWrapper
 * with module access check and i18n locale handling.
 */
export function AuthWrapper({ children }: AuthWrapperProps) {
    const { i18n } = useLingui()

    return (
        <SharedAuthWrapper
            hasModuleAccess={hasModuleFeatureAccess}
            AccessDeniedComponent={AccessDenied}
            LoadingComponent={LoadingSpinner}
            AccessDeniedLayout={AccessDeniedLayout}
            currentLocale={i18n.locale}
            onLocaleChange={(userLang, currentLang) => {
                const lang = userLang as SupportedLocale
                if (lang in locales && lang !== currentLang) {
                    loadCatalog(lang)
                }
            }}
        >
            {children}
        </SharedAuthWrapper>
    )
}
